import React, { useEffect, useState } from "react";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Flag,
  Building,
  Link2,
  Share2,
  Calendar,
  Users,
  Edit,
  ShieldCheck,
  FileText,
  Hash,
} from "lucide-react";
import api from "../../../../api/axiosInstance";
import { useSelector } from "react-redux";
import EditProfile from "./EditProfile";
import "../dashboard.css";

const UserProfile = () => {
  const { accessToken } = useSelector((state) => state.merchant);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);

  /* ---------------- FETCH USER PROFILE ---------------- */
  const fetchProfile = async () => {
    try {
      const res = await api.get("/api/auth/me");
      setProfile(res.data.data);
    } catch (err) {
      console.error("Profile fetch failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    fetchProfile();
  }, [accessToken]);

  const handleProfileUpdate = () => {
    // Refresh profile after update
    setLoading(true);
    fetchProfile();
  };

  if (loading) return <div className="text-center p-5">Loading profile...</div>;

  if (!profile)
    return (
      <div className="text-center text-danger p-5">
        Failed to load profile.
      </div>
    );

  return (
    <>
      <div className="overview-container">
        <div className="container-fluid">

          {/* PROFILE HEADER */}
          <div className="merchant-profile-header">
            <div className="profile-image">
              {profile.photoUrl ? (
                <img
                  src={`${process.env.REACT_APP_IMAGE_BASE_URL}${profile.photoUrl}`}
                  alt="Profile"
                  className="profile-photo"
                />
              ) : (
                <User size={34} />
              )}
            </div>

            <h1 className="profile-merchant">{profile.fullName}</h1>
            <div className="merchant-profile-phone">{profile.mobileNumber}</div>
            <div className="merchant-profile-email">{profile.email}</div>

            {/* Edit Button */}
           <button
  className="btn-edit-profile"
  onClick={() => setShowEditModal(true)}
>
  <Edit size={18} className="icon" />
  <span>Edit Profile</span>
</button>
          </div>

          {/* PERSONAL INFORMATION */}
          <div className="merchnat-info-section">
            <h2 className="merchnat-info-section-title">
              <User size={20} /> Personal Information
            </h2>

            <div className="merchant-info-grid personal-grid">
              <InfoItem icon={<User size={18} />} label="Full Name" value={profile.fullName} />
              <InfoItem icon={<Phone size={18} />} label="Mobile Number" value={profile.mobileNumber} />
              <InfoItem icon={<Mail size={18} />} label="Email" value={profile.email || "N/A"} />
              <InfoItem
                icon={<Users size={18} />}
                label="Son / Daughter Of"
                value={profile.sonDaughterOf || "N/A"}
              />
              <InfoItem
                icon={<Calendar size={18} />}
                label="Date of Birth"
                value={profile.dob ? new Date(profile.dob).toLocaleDateString() : "N/A"}
              />
            </div>
          </div>

          {/* ADDRESS INFORMATION */}
          <div className="merchnat-info-section">
            <h2 className="merchnat-info-section-title">
              <MapPin size={20} /> Address Information
            </h2>

            <div className="merchant-info-grid personal-grid">
              <InfoItem
                icon={<MapPin size={18} />}
                label="Street Address"
              value={profile.address?.streetAddress || "N/A"}
              />
              <InfoItem
                icon={<Flag size={18} />}
                label="Country"
              value={profile.address?.country?.name || "N/A"}
              />
              <InfoItem
                icon={<Building size={18} />}
                label="State"
                value={profile.address?.state?.name || "N/A"}
              />
              <InfoItem
                icon={<MapPin size={18} />}
                label="District"
                value={profile.address?.district?.name || "N/A"}
              />
              <InfoItem
                icon={<MapPin size={18} />}
                label="Local Area"
                value={profile.address?.localArea?.name || "N/A"}
              />
              <InfoItem
                icon={<Hash size={18} />}
                label="Pincode"
                value={profile.kyc?.pincode || "N/A"}
              />
            </div>
          </div>

          {/* KYC DOCUMENT INFORMATION */}
          <div className="merchnat-info-section">
            <h2 className="merchnat-info-section-title">
              <ShieldCheck size={20} /> KYC Documents
            </h2>

            <div className="merchant-info-grid personal-grid">
              <InfoItem
                icon={<Hash size={18} />}
                label="Aadhaar Number"
                value={profile.kyc?.aadhaarNumber || "N/A"}
              />
              <InfoItem
                icon={<Hash size={18} />}
                label="PAN Number"
                value={profile.kyc?.panNumber || "N/A"}
              />
            </div>

            <div className="kyc-docs-grid">
              <KycDocItem
                label="Aadhaar Front"
                url={profile.kyc?.aadhaarFront}
              />
              <KycDocItem
                label="Aadhaar Back"
                url={profile.kyc?.aadhaarBack}
              />
              <KycDocItem
                label="PAN Front"
                url={profile.kyc?.panFront}
              />
              <KycDocItem
                label="PAN Back"
                url={profile.kyc?.panBack}
              />
            </div>
          </div>

          {/* SOCIAL INFORMATION */}
          <div className="merchnat-info-section">
            <h2 className="merchnat-info-section-title">
              <Share2 size={20} /> Social Information
            </h2>

            <div className="merchant-info-grid personal-grid">
              <InfoItem 
                icon={<Link2 size={18} />} 
                label="Facebook" 
                value={profile.socialLinks?.facebookProfile || "N/A"} 
                isLink={true}
              />
              <InfoItem 
                icon={<Link2 size={18} />} 
                label="Twitter" 
                value={profile.socialLinks?.twitterProfile || "N/A"} 
                isLink={true}
              />
              <InfoItem 
                icon={<Link2 size={18} />} 
                label="Instagram" 
                value={profile.socialLinks?.instagramProfile || "N/A"} 
                isLink={true}
              />
            </div>
          </div>

        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <EditProfile 
              onClose={() => setShowEditModal(false)}
              onSave={handleProfileUpdate}
            />
          </div>
        </div>
      )}
    </>
  );
};

/* ---------- KYC DOCUMENT ITEM ---------- */
const KycDocItem = ({ label, url }) => {
  const baseUrl = process.env.REACT_APP_IMAGE_BASE_URL || '';
  const fullUrl = url ? `${baseUrl}${url}` : null;
  const isPdf = url && url.toLowerCase().endsWith('.pdf');

  return (
    <div className="kyc-doc-item">
      <div className="kyc-doc-label">
        <FileText size={16} />
        <span>{label}</span>
      </div>
      {fullUrl ? (
        isPdf ? (
          <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="kyc-doc-link">
            View PDF
          </a>
        ) : (
          <a href={fullUrl} target="_blank" rel="noopener noreferrer">
            <img src={fullUrl} alt={label} className="kyc-doc-img" />
          </a>
        )
      ) : (
        <span className="kyc-doc-empty">Not uploaded</span>
      )}
    </div>
  );
};

/* ---------- REUSABLE INFO ITEM ---------- */
const InfoItem = ({ icon, label, value, isLink = false }) => (
  <div className="merchant-info-item">
    <div className="info-icon">{icon}</div>
    <div className="info-content">
      <div className="info-label">{label}</div>
      <div className="info-value">
        {isLink && value !== "N/A" ? (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ) : (
          value
        )}
      </div>
    </div>
    <style>{`
    .kyc-docs-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 16px;
}

.kyc-doc-item {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.kyc-doc-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
}

.kyc-doc-img {
  width: 100%;
  max-height: 160px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.kyc-doc-link {
  display: inline-block;
  padding: 6px 14px;
  background: #6366f1;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  text-decoration: none;
  width: fit-content;
}

.kyc-doc-empty {
  font-size: 13px;
  color: #9ca3af;
  font-style: italic;
}

@media (max-width: 576px) {
  .kyc-docs-grid {
    grid-template-columns: 1fr;
  }
}

    .btn-edit-profile {
  display: inline-flex;
  align-items: center;
  gap: 10px;

  padding: 10px 18px;
  border-radius: 999px;

  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;

  font-size: 14px;
  font-weight: 600;

  border: none;
  cursor: pointer;

  box-shadow: 0 8px 20px rgba(99, 102, 241, 0.25);
  transition: all 0.25s ease;
}

.btn-edit-profile .icon {
  transition: transform 0.25s ease;
}

/* Hover effect */
.btn-edit-profile:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(99, 102, 241, 0.35);
  opacity: 0.95;
}

/* Icon animation */
.btn-edit-profile:hover .icon {
  transform: rotate(-8deg) scale(1.1);
}

/* Active click */
.btn-edit-profile:active {
  transform: translateY(0);
  box-shadow: 0 6px 16px rgba(99, 102, 241, 0.2);
}

    `}</style>
  </div>
);

export default UserProfile;